import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";

import uiConf from "../../config/uiConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import { debounce } from "lodash";

const TkOutSourcingShipDetailTarget = function (props) {
    const view = "TkVwOutSourcingShipDetailTargetList";

    const { id, mstTargetSelect, onDetailTargetListGrid, onDetailTargetListApi, onDetailTargetSelect } = props;

    const { t } = useTranslation();

    const [tkOutSourcingShipDetailTargetGrid, setTkOutSourcingShipDetailTargetGrid] = useState();
    const [tkOutSourcingShipDetailTargetApi, setTkOutSourcingShipDetailTargetApi] = useState();
    const [tkOutSourcingShipDetailTargetSelect, setTkOutSourcingShipDetailTargetSelect] = useState();

    useEffect(() => {
        (async () => {
            if (tkOutSourcingShipDetailTargetGrid) {
                tkOutSourcingShipDetailTargetGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingShipDetailTargetSelect(grid.selectedItems[0]);
                            if (onDetailTargetSelect) {
                                onDetailTargetSelect(grid.selectedItems[0]);
                            }
                        } else {
                            setTkOutSourcingShipDetailTargetSelect();
                            if (onDetailTargetSelect) {
                                onDetailTargetSelect();
                            }
                        }
                    }),
                    10
                );

                tkOutSourcingShipDetailTargetGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingShipDetailTargetSelect(grid.selectedItems[0]);
                            if (onDetailTargetSelect) {
                                onDetailTargetSelect(grid.selectedItems[0]);
                            }
                        } else {
                            setTkOutSourcingShipDetailTargetSelect();
                            if (onDetailTargetSelect) {
                                onDetailTargetSelect();
                            }
                        }
                    }),
                    10
                );
            }
        })();
    }, [tkOutSourcingShipDetailTargetGrid, onDetailTargetSelect]);

    useEffect(() => {
        if (tkOutSourcingShipDetailTargetApi) {
            tkOutSourcingShipDetailTargetApi.refresh();
        }
    }, [mstTargetSelect.outSourcingId, tkOutSourcingShipDetailTargetApi]);

    return (
        <BasicCrudScreenType
            id={id}
            headerTitle={t("term.tkVwOutSourcingShipDetailTargetList")}
            view={view}
            onInitialized={(grid) => {
                grid.selectionMode = "Row";
                setTkOutSourcingShipDetailTargetGrid(grid);
                if (onDetailTargetListGrid) {
                    onDetailTargetListGrid(grid);
                }
            }}
            onApiInitialized={(api) => {
                setTkOutSourcingShipDetailTargetApi(api);
                if (onDetailTargetListApi) {
                    onDetailTargetListApi(api);
                }
            }}
            onModifyViewUrl={(url) => `${url}/${mstTargetSelect.outSourcingId}`}
            embededInOtherScreenType
            noCreate
            noEdit
            noDelete
            noExcel
        />
    );
};

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function TkOutSourcingShipDetailDialog(props) {
    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        title,
        formId,

        modifyUrlCallback,

        customizeErrorMsgCallback,
        onSelectMst,
        onDialogUpdateDetail,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [mstTargetListGrid, setMstTargetListGrid] = useState();
    const [mstTargetListApi, setMstTargetListApi] = useState();
    const [mstTargetSelect, setMstTargetSelect] = useState();

    const [detailTargetListGrid, setDetailTargetListGrid] = useState();
    const [detailTargetListApi, setDetailTargetListApi] = useState();
    const [detailTargetSelect, setDetailTargetSelect] = useState();

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    if (schema && !schema.columnMap) {
        let columnMap = {};

        for (const column of schema.columns) {
            columnMap[column.name] = column;
        }

        schema.columnMap = columnMap;
    }

    useEffect(() => {
        (async () => {
            if (mstTargetListGrid) {
                mstTargetListGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMstTargetSelect(grid.selectedItems[0]);
                        } else {
                            setMstTargetSelect();
                        }
                    }),
                    10
                );

                mstTargetListGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMstTargetSelect(grid.selectedItems[0]);
                        } else {
                            setMstTargetSelect();
                        }
                    }),
                    10
                );
            }
        })();
    }, [mstTargetListGrid]);

    useEffect(() => {
        if (mstTargetSelect && detailTargetSelect) {
            (async () => {
                setFormData((formData) => {
                    let newFormData = {
                        ...formData,

                        orderBuyerId: mstTargetSelect.orderBuyerId,
                        orderBuyerName: mstTargetSelect.orderBuyerName,
                        workOrderNumber: mstTargetSelect.workOrderNumber,
                        modelName: mstTargetSelect.modelName,
                        modelPartName: mstTargetSelect.modelPartName,

                        materialCode: detailTargetSelect.materialCode,
                        materialCodeVer: detailTargetSelect.materialCodeVer,
                        materialName: detailTargetSelect.materialName,
                        partNo: detailTargetSelect.partNo,
                        partSeq: detailTargetSelect.partSeq,
                        opCode: detailTargetSelect.opCode,
                        cutCode1: detailTargetSelect.cutCode1,
                        cutCode2: detailTargetSelect.cutCode2,
                        material: detailTargetSelect.material,
                        itemSpecName: detailTargetSelect.itemSpecName,
                        itemUnitName: detailTargetSelect.itemUnitName,
                        t_size: detailTargetSelect.t_size,
                        t_delta: detailTargetSelect.t_delta,
                        w_size: detailTargetSelect.w_size,
                        w_delta: detailTargetSelect.w_delta,
                        l_size: detailTargetSelect.l_size,
                        l_delta: detailTargetSelect.l_delta,
                        startProcessName: detailTargetSelect.startProcessName,
                        endProcessName: detailTargetSelect.endProcessName,

                        outSourcingShipId: onSelectMst.outSourcingShipId,
                        outSourcingId: detailTargetSelect.outSourcingId,
                        outSourcingDetailId: detailTargetSelect.outSourcingDetailId,
                        outSourcingShipQty: detailTargetSelect.outSourcingQty,
                    };

                    for (const column of schema.columns) {
                        if (column?.defaultValue) {
                            newFormData[column.name] = column.defaultValue;
                        }
                    }

                    setOkButtonDisabled(false);

                    return newFormData;
                });
            })();
        }
    }, [onSelectMst, mstTargetSelect, detailTargetSelect, schema]);

    const checkItemValidataion = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("create", column, column.validation, formData[column.name] || "");

            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        console.error(msg);

        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const nofifyWarning = (msg) => {
        console.warn(msg);

        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);

        if (onDialogUpdateDetail) {
            onDialogUpdateDetail();
        }
    };

    const onDialogInitialized = (dialogInstance) => {
        setDialogInstance(dialogInstance);

        if (onInitialized) {
            onInitialized(dialogInstance);
        }
    };

    const onApiSuccess = (result) => {
        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            }

            if (detailTargetListApi) {
                detailTargetListApi.refresh();
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(result.data), 0);
            } else {
                onNotifySuccess(result.data);
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            notifyFailure(customizeErrorMsgCallback(reason));
        } else {
            notifyFailure(
                t("error.insertFailure", { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidataion(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url = `/api/${schema.name}`;

        if (modifyUrlCallback) {
            url = modifyUrlCallback(url);
        }

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (apiData[formItem] !== undefined) {
                json[formItem] = apiData[formItem];
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setMstTargetSelect();
        setDetailTargetSelect();

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {
        setTimeout(() => {
            setOkButtonDisabled(false);

            setFormData(formData);
        }, 0);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setMstTargetSelect();
        setDetailTargetSelect();

        setFormData(null);

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.5}>
                        <ReflexContainer orientation="horizontal">
                            <ReflexElement flex={0.4}>
                                <BasicCrudScreenType
                                    id={`${id}-mst-target`}
                                    embededInOtherScreenType
                                    headerTitle={t("term.tkVwOutSourcingShipMstTargetList")}
                                    view="TkVwOutSourcingShipMstTargetList"
                                    noCreate
                                    noEdit
                                    noDelete
                                    noExcel
                                    onInitialized={(grid) => {
                                        grid.selectionMode = "Row";
                                        setMstTargetListGrid(grid);
                                    }}
                                    onApiInitialized={(api) => {
                                        setMstTargetListApi(api);
                                    }}
                                    onModifyViewUrl={(url) => `${url}/${onSelectMst.outSourcingCompany}`}
                                />
                            </ReflexElement>

                            <ReflexSplitter style={{ height: "1px", color: "lightgray" }} />

                            <ReflexElement flex={0.6}>
                                {mstTargetSelect ? (
                                    <TkOutSourcingShipDetailTarget
                                        id={`${id}-detail-target`}
                                        mstTargetSelect={mstTargetSelect}
                                        onDetailTargetListGrid={setDetailTargetListGrid}
                                        onDetailTargetListApi={setDetailTargetListApi}
                                        onDetailTargetSelect={setDetailTargetSelect}
                                    />
                                ) : (
                                    <></>
                                )}
                            </ReflexElement>
                        </ReflexContainer>
                    </ReflexElement>

                    <ReflexSplitter style={{ width: "1px", color: "lightgray" }} />

                    <ReflexElement flex={0.5}>
                        <div
                            style={{
                                padding: "16px",
                            }}
                        >
                            <AutoForm
                                id="outsourcing-ship-detail-create-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default TkOutSourcingShipDetailDialog;
